<template>
  <div
    v-observe-visibility="visibilityChanged"
    :data-distance-label="`${this.widthInMilliseconds}ms`"
    class="zmanim-solar-path__container"
  >
    <div
      ref="solarPath"
      :data-start-label="`${this.startingMilliseconds / 1000}s (2π)`"
      class="zmanim-solar-path__image"
    >
      <div class="zmanim-solar-path__clipping-mask" style="--clip-z-index: 20;">
        <div :style="clippingMaskAboveHorizon">
          <zmanim-solar-placemark
            key="`now"
            :style="styleForNow"
            :now="true"
            v-bind="nowMarker"
          />
        </div>
        <div :style="clippingMaskBelowHorizon">
          <zmanim-solar-placemark
            key="`now"
            :style="styleForNow"
            :now="true"
            :day="false"
            v-bind="nowMarker"
          />
        </div>
      </div>
      <div class="zmanim-solar-path__landmarks">
        <zmanim-solar-placemark
          v-for="(marker, index) in markers"
          :key="`z${index}`"
          :style="styleForNodeAtIndex(indexForZman(marker))"
          v-bind="marker"
        />
      </div>
    </div>
    <div :style="this.horizonStyle" class="zmanim-solar-path__horizon" />
  </div>
</template>

<script>
import { brooklyn } from '@/lib/locations'
import DateBasedAnimation from '@/lib/mixins/dateBasedAnimation'
import ZmanimList from '@/lib/zmanimList'
import * as util from '@/lib/util'

import moment from 'moment'
import ZmanimSolarPlacemark from './ZmanimSolarPlacemark'

const oneRemInPx = parseFloat(
  getComputedStyle(document.documentElement).fontSize
)
export default {
  name: 'ZmanimSolarPath',
  components: {
    ZmanimSolarPlacemark
  },
  mixins: [DateBasedAnimation],
  props: {
    location: {
      type: Object,
      default: () => brooklyn
    }
  },
  data () {
    return {
      size: {
        width: 1024,
        height: 309
      }
    }
  },
  computed: {
    options () {
      return this.location
    },
    isMidday () {
      const ms = this.date.valueOf()
      const isDaylight = this.msIsBetweenSunriseAndSunset(ms)
      return isDaylight
    },
    styleForNow () {
      const index = this.indexForDate(this.date)
      const style = this.styleForNodeAtIndex(index)
      style.marginLeft = `${index}px`
      return style
    },
    nowMarker () {
      return {
        title: this.location.options.locationName,
        background: this.backgroundForDate(this.date),
        time: moment().format('h:mm:ssa')
      }
    },
    chatzosYom () {
      const chatzos = this.zmanim.zmanForKey('Chatzos')
      return chatzos.date
    },
    /**
     * Returns 12 hours before chatzos yom.
     */
    startingMilliseconds () {
      const midnight = this.chatzosYom
      midnight.setHours(midnight.getHours() - 12)
      const midnightMilliseconds = midnight.valueOf()
      return midnightMilliseconds
    },
    endingMilliseconds () {
      const midnight = this.chatzosYom
      midnight.setHours(midnight.getHours() + 12)
      const midnightTomorrowMilliseconds = midnight.valueOf()
      return midnightTomorrowMilliseconds
    },
    zmanim () {
      const location = this.location
      const zmanimList = new ZmanimList(location)
      return zmanimList
    },
    /**
     * Computes an array of markers for the zmanim.
     */
    markers () {
      const zmanim = this.zmanim.zmanimItems

      const markers = zmanim
        .map(zman => {
          const title = zman.title
          const time = zman.time
          const date = zman.date
          const ms = date.valueOf()
          const millisecondsSinceStart = ms - this.startingMilliseconds
          const x = millisecondsSinceStart / this.millisecondsPerPixel

          const y = 0
          // console.log('timeString', timeString); // eslint-disable-line
          // console.log('timeString', timeMoment.creationData()); // eslint-disable-line
          // debugger;
          const marker = {
            title,
            time,
            millisecondsSinceStart,
            x,
            y,
            background: this.backgroundForDate(date),
            clip: this.isMidday
          }
          return marker
        })
        .filter(marker => !isNaN(marker.x) && !isNaN(marker.y))
      // console.log('Mapped: ', markers); // eslint-disable-line
      return markers
    },
    /**
     * The range of milliseconds represented by the graph. (Should be 86400000.)
     */
    widthInMilliseconds () {
      return 86400000
    },
    /**
     * The number of milliseconds each point represents.
     * (px/pt are being used interchangeably here.)
     */
    millisecondsPerPixel () {
      const width = this.size.width
      return this.widthInMilliseconds / width
    },
    /**
     * The same as above, but reverse ratio.
     */
    pixelsPerMillisecond () {
      const width = this.size.width
      return width / this.widthInMilliseconds
    },
    /**
     * The number of degrees represented by a pixel.
     */
    degreesPerPixel () {
      const width = this.size.width
      const phaseOfSine = 2 * Math.PI
      const degreesPerPixel = phaseOfSine / width

      return degreesPerPixel
    },
    /**
     * Returns a  mask for the area above the horizon.
     */
    clippingMaskAboveHorizon () {
      const bottom = this.horizonOffset
      const isMidday = this.isMidday
      const clipPath = isMidday ? '' : `inset(0 0 ${bottom}px 0)`
      const overflow = isMidday ? 'visible' : 'hidden'

      return {
        position: 'absolute',
        margin: 0,
        height: '100%',
        width: '100%',
        '-webkit-clip-path': clipPath,
        'clip-path': clipPath,
        overflow
        // backgroundColor: '#ff0000',
        // border: '5px solid red',
      }
    },
    /**
     * Returns a  mask for the area below the horizon.
     */
    clippingMaskBelowHorizon () {
      const top = this.size.height - this.horizonOffset
      return {
        position: 'absolute',
        overflow: 'hidden',
        margin: 0,
        height: '100%',
        width: '100%',
        '-webkit-clip-path': `inset(${top}px 0 0 0)`,
        'clip-path': `inset(${top}px 0 0 0)`
        // backgroundColor: '#ff0000',
        // border: '5px solid blue',
        // background: 'linear-gradient(#111111, #130904)',
      }
    },
    sunriseMarker () {
      return this.markers.find(marker => marker.title === 'Sunrise')
    },
    horizonOffset () {
      const index = this.indexForZman(this.sunriseMarker)
      const margin = this.size.height - (this.yForX(index) - 2.0)
      return margin
    },
    horizonStyle () {
      return {
        '--zmn-margin-top': `-${this.horizonOffset}px`,
        '--zmn-horizon-height': `${this.horizonOffset}px`
      }
    }
  },
  created () {
    window.onresize = this.adjustSize
    this.animate()
  },
  mounted () {
    this.$nextTick(this.adjustSize)
  },
  methods: {
    backgroundForDate (date) {
      const daylight = this.msIsBetweenSunriseAndSunset(date.valueOf())
      return daylight ? 'light' : 'dark'
    },
    msIsBetweenSunriseAndSunset (ms) {
      const sunrise = this.zmanim.zmanForKey('Sunrise')
      const sunset = this.zmanim.zmanForKey('Sunset')

      if (!sunrise || !sunrise) {
        // console.warn(`Object for sunrise (${sunrise})or sunset (${sunset}) is undefined.`);
        return false
      }

      const dark = sunrise.date.valueOf() < ms && sunset.date.valueOf() > ms
      // debugger;
      return dark
    },
    adjustSize () {
      this.size = {
        height: this.$el.clientHeight,
        width: this.$el.clientWidth
      }
    },
    millisecondsSinceStart (string) {
      const milliseconds = Date.parse(string)
      const offset = milliseconds - this.startingMilliseconds
      return offset
    },
    yForX (x) {
      const yShiftInPx = this.size.height / 2
      const xTerm = x / (this.size.width / 2 / Math.PI) - Math.PI / 2
      const y = -1 * Math.sin(xTerm) * yShiftInPx + yShiftInPx
      return y
    },
    styleForNodeAtIndex (index) {
      const x = index
      const y = this.yForX(x)
      let opacity = 1
      const threshold = 4.0 * oneRemInPx
      const distanceBeneathHorizon = Math.abs(index / 2 - this.size.width / 2)

      if (distanceBeneathHorizon <= threshold) {
        opacity = distanceBeneathHorizon / threshold
      } else if (index < threshold) {
        opacity = index / threshold
      }

      const style = {
        position: 'absolute',
        marginLeft: `${x}px`,
        marginTop: `${y}px`,
        opacity,
        transform: `scale(${opacity})`
      }
      // debugger;
      return style
    },
    /**
     * Returns an index for a zman, based on its `time` property.
     */
    indexForZman (zman) {
      const pctOfDay =
        util.percentOfDay(zman.millisecondsSinceStart) * this.size.width
      // console.log(`pct of day @ ${zman.title}`,
      // pctOfDay / this.size.width); // eslint-disable-line
      return pctOfDay
    },
    indexForDate (date) {
      const millisecondsSinceStart = date.valueOf() - this.startingMilliseconds
      const pctOfDay =
        util.percentOfDay(millisecondsSinceStart) * this.size.width
      // console.log(`pct of now `, pctOfDay / this.size.width); // eslint-disable-line
      return pctOfDay
    }
  }
}
</script>

<style scoped>
.zmanim-solar-path__container {
  height: 19.44rem;
  width: 100%;
  margin-bottom: 0;
  margin-top: 5.13rem;
  padding: 0;
}

.zmanim-solar-path__image {
  position: relative;
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  padding: 0;
  margin: 0;
  background-image: url("/static/solar_path/FadeToDark@3x.png");
  background-repeat: no-repeat;
  background-repeat: repeat-x;
  background-size: 100% 19.44rem;
  z-index: 1;
}

.zmanim-solar-path__clipping-mask {
  position: absolute;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  flex-grow: 0;
}

.zmanim-solar-path__landmarks {
  position: absolute;
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  /* transition: opacity 200ms ease, scale 200ms ease; */
}

.zmanim-solar-path__horizon {
  border-top: 3px solid #ff8511;
  margin-top: var(--zmn-margin-top);
  background: #111;
  height: var(--zmn-horizon-height);
  margin-left: -13.31rem;
  /* position: absolute; */
}
</style>
